<template>
  <div id="shared-databanks">
    <div class="flex">
      <div
        class="sticky left-0 z-30 flex items-center justify-between px-6 py-3"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <div class="flex items-center gap-2">
          <BlTextTitle2 text="Shared databanks" class="text-dark-purple-100" />
        </div>
        <div class="flex items-center gap-5">
          <button
            class="transition-all duration-300 outline-none import-btn"
            @click="importOpened = true"
          >
            Import from center
          </button>
          <BlInputButton
            icon="plus"
            label="New databank"
            @clicked="newOpened = true"
          />
        </div>
      </div>
    </div>
    <ImportDatabankModal
      v-if="importOpened"
      :centers="centers"
      @close-modal="importOpened = false"
    />
    <NewDatabankModal v-if="newOpened" @close-modal="newOpened = false" />
    <div class="flex">
      <div
        class="sticky left-0 z-30 flex items-center gap-2 px-6 py-3"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <BlInputSearch
          class="w-80"
          :input="searchInput"
          @changed="onInputSearch"
        />
        <BlSort
          class="w-60"
          v-model="sortInput"
          :options="sortOptions"
          :defaultValue="defaultSort"
          @change="onChangeSort($event)"
        />
      </div>
    </div>

    <div v-if="pageLoading" class="grid h-128 place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="!databanks.length"
      class="flex flex-col items-center justify-center h-128"
    >
      <BlTextBody1
        class="text-center"
        text="No databanks have been created yet. Start by adding a new databank to get started."
      />
    </div>
    <div v-else class="mb-8">
      <div
        id="databanks-header"
        class="sticky z-20 flex items-center flex-grow rounded-t top-20 bg-slate-300"
      >
        <div class="sticky left-0 z-10 flex pl-6 bg-slate-100">
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t border-l border-r rounded-tl w-80 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Name" class="truncate" />
          </BlTooltip>
        </div>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <BlTextSubtitle3 text="Total curricula" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <BlTextSubtitle3 text="Total targets" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
        >
          <BlTextSubtitle3 text="Description" class="truncate" />
        </BlTooltip>
      </div>

      <DatabankItem
        v-for="(databank, idx) in databanks"
        :key="databank.id"
        :databank="databank"
        :isLast="idx + 1 === databanks.length"
      />
      <div v-if="totalDatabanks > 25" class="flex mb-36">
        <BlInputPagination
          class="sticky left-6"
          :style="{ width: 'calc(100vw - 3rem - 0.5rem)' }"
          :page="page"
          :perPage="perPage"
          :total="totalDatabanks"
          @changed="changePage"
          @changed-per-page="changePerPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import { displayDate, displayPrice } from "@/plugins/helper.js";
import { mapActions, mapGetters } from "vuex";
import BlChip from "../../BlChip.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputFilter from "../../BlInputFilter.vue";
import BlInputPagination from "../../BlInputPagination.vue";
import BlInputSearch from "../../BlInputSearch.vue";
import BlSort from "../../BlSort.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextTitle2 from "../../BlTextTitle2.vue";
import BlTooltip from "../../BlTooltip.vue";
import ImportDatabankModal from "./ImportDatabankModal.vue";
import NewDatabankModal from "./NewDatabankModal.vue";
import DatabankItem from "./Item.vue";

export default {
  name: "SharedDatabanks",
  components: {
    ImportDatabankModal,
    BlTextTitle2,
    NewDatabankModal,
    BlTextSubtitle3,
    DatabankItem,
    BlTextBody1,
    BlTextBody2,
    BlTooltip,
    BlChip,
    BlInputButton,
    BlInputSearch,
    BlInputFilter,
    BlSort,
    BlInputPagination
  },
  data() {
    const defaultSort = { primary: "date", secondary: "desc" };
    return {
      defaultSort,
      pageLoading: true,
      importOpened: false,
      newOpened: false,
      page: 1,
      perPage: 25,
      searchInput: { placeholder: "Search by name", model: "" },
      filterInput: {
        model: [[], []],
        tabs: [
          {
            name: "Center status",
            kind: "multi-tag",
            options: [
              {
                name: "Active",
                value: "active",
                color: "#457C0F",
                backgroundColor: "#F5FEE7"
              },
              {
                name: "Deactivated",
                value: "deactivated",
                color: "#564389",
                backgroundColor: "#F3F0F7"
              }
            ]
          }
          // {
          //   name: "Payment status",
          //   kind: "multi-tag",
          //   options: [
          //     {
          //       name: "Paid",
          //       value: "paid",
          //       color: "#457C0F",
          //       backgroundColor: "#D3F99D"
          //     },
          //     {
          //       name: "Unpaid",
          //       value: "unpaid",
          //       color: "#0172A3",
          //       backgroundColor: "#B9EAFE"
          //     },
          //     {
          //       name: "Overdue",
          //       value: "overdue",
          //       color: "#DD3F30",
          //       backgroundColor: "#FECFCA"
          //     }
          //   ]
          // }
        ]
      },
      sortInput: defaultSort,
      sortOptions: {
        primary: [
          { value: "date", label: "Date created" },
          { value: "name", label: "Name" }
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "databanks", "totalDatabanks", "centers"]),
    getParams() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const query = this.searchInput.model;
      const databankStatues = this.filterInput.model[0];
      // const paymentStatues = this.filterInput.model[1]
      const sort = this.sortInput;

      if (query) params += `&query=${query}`;
      if (databankStatues.length > 0) {
        params += `&status=${databankStatues.map(a => a.value).join(",")}`;
      }

      const sorts = {
        date: { asc: "oldest", desc: "newest" },
        name: { asc: "name_asc", desc: "name_desc" }
      };
      params += `&sort=${sorts[sort.primary][sort.secondary]}`;

      return params;
    }
  },
  watch: {},
  async mounted() {
    this.pageLoading = true;
    await this.getDatabanks();
    const searchParams = this.$route.query;

    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["name"]) {
      this.searchInput.model = searchParams["name"];
    }

    const filter = [[]];
    if (searchParams["status"]) {
      const arr = searchParams["status"].split(",").map(a => {
        const opts = this.filterInput.tabs[0].options;
        return opts.find(i => i.value === a) || { value: a };
      });
      filter[0] = arr;
    }
    this.filterInput.model = filter;

    // prettier-ignore
    const sorts = {
      oldest: { primary: "date", secondary: "asc" },
      newest: { primary: "date", secondary: "desc" },
      name_asc: { primary: "name", secondary: "asc" },
      name_desc: { primary: "name", secondary: "desc" }
    };
    if (searchParams["sort"]) {
      const sort = searchParams["sort"] || "";
      if (sort && sorts[sort]) {
        this.sortInput = sorts[sort];
      }
    }

    this.fetchDatabanks();
    this.fetchCenters();
  },
  destroyed() {
    const app = document.getElementById("app");
    if (app) app.scroll({ top: 0, left: 0 });
  },
  methods: {
    ...mapActions(["getDatabanks", "getCenters"]),
    displayDate,
    displayPrice,

    fetchCenters() {
      this.getCenters();
    },

    fetchDatabanks() {
      this.pageLoading = true;
      this.getDatabanks({ params: this.getParams }).then(() => {
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/super-admin/databank${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },

    //
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchDatabanks();
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchDatabanks();
    },
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchDatabanks();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchDatabanks();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.fetchDatabanks();

      this.perPage = perPage;
    }
  }
};
</script>

<style scoped lang="scss">
.import-btn {
  width: auto;
  padding: 0 16px;
  height: 2.375rem;
  background: var(--Greyscale-Pure-White, #fff);
  border: 1px solid var(--primary-color);
  box-sizing: border-box;
  color: var(--primary-color);
  font-weight: 600;
  border-radius: 5px;
  &:hover {
    background-color: var(--grey-two);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/superAdmin/databanks/SharedDatabanks.vue